import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { messages } from '../localization/messages.ts';
import { TurnRail } from './TurnRail.tsx';
import { StageDock, type StageDockProps } from './StageDock.tsx';
import { StagePage } from './StagePage.tsx';
import { StageCanvasTransition } from './StageCanvasTransition.tsx';
import { StageActivity, StageActivityTrigger } from './StageActivity.tsx';
import { useStageViewport } from './use-keyboard-lift.ts';
import { activitySurfaceNavigationTargets, activitySurfaceTurnIndex } from './activity-context.ts';
import { formatNarrationLine } from './narration-line.ts';
import { ComposerDraftContext } from './ComposerDraftContext.tsx';
import type { StageController } from './useStageController.ts';
import './stage-effects.css';

interface StageLayoutProps {
  stage: StageController;
  header: ReactNode;
  placeholder?: MessageDescriptor;
  canvasNotice?: ReactNode;
  workspaceRef?: Ref<HTMLDivElement>;
  renderDock?: (props: StageDockProps) => ReactNode;
  children: ReactNode;
}

/** Shared page, history and persistent composer. Entries supply their surrounding controls. */
export const StageLayout = observer(function StageLayout({
  stage,
  header,
  placeholder,
  canvasNotice,
  workspaceRef,
  renderDock,
  children,
}: StageLayoutProps) {
  const intl = useIntl();
  const store = useMessagingStore();
  const viewport = useStageViewport();
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const activityTriggerRef = useRef<HTMLButtonElement>(null);
  const [dockHeight, setDockHeight] = useState(0);
  const {
    turns,
    surfaceEntries,
    loadingLine,
    errorNotice,
    currentRailIndex,
    activityContext,
    viewingHistory,
    activityStatus,
    activityOpen,
    setActivityOpen,
    setSelectedIndex,
    dismissedErrorRunId,
    setDismissedErrorRunId,
    pendingRequest,
    runInFlight,
    voiceActive,
    dockNarration,
  } = stage;

  const pageKey = stage.view.key;
  // biome-ignore lint/correctness/useExhaustiveDependencies: page identity resets the active scroller.
  useEffect(() => {
    const canvas = canvasScrollRef.current;
    if (canvas && getComputedStyle(canvas).overflowY === 'visible') {
      window.scrollTo(0, 0);
    } else {
      canvas?.scrollTo(0, 0);
    }
  }, [pageKey]);

  const dockProps: StageDockProps = {
    onHeightChange: setDockHeight,
    ready: stage.ready,
    activity: (
      <StageActivityTrigger
        status={activityStatus}
        detail={
          runInFlight && pendingRequest === null
            ? formatNarrationLine(store.narration.detail, intl)
            : undefined
        }
        working={
          runInFlight || loadingLine?.kind === 'connecting' || loadingLine?.kind === 'preparing'
        }
        open={activityOpen && !viewport.compact}
        available={
          Boolean(activityContext.responseId) || store.narration.runs.size > 0 || activityOpen
        }
        triggerRef={activityTriggerRef}
        onToggle={() => setActivityOpen((open) => viewport.compact || !open)}
      />
    ),
    voiceActive,
    placeholder: placeholder ? intl.formatMessage(placeholder) : undefined,
    narration: voiceActive ? dockNarration.text : activityStatus,
    narrationVariant: dockNarration.variant,
    isUserRequestPending: runInFlight,
    onSend: stage.sendMessage,
    onStopStreaming: store.stopStreaming,
  };
  return (
    <ComposerDraftContext.Provider value={stage.composerDraft}>
      <div
        className="stage relative bg-background text-foreground"
        data-activity-open={activityOpen}
        data-keyboard-compact={viewport.compact}
        style={viewport.style}
      >
        {children}
        <div ref={workspaceRef} className="stage-workspace relative min-h-0 min-w-0">
          <TurnRail turns={turns} currentIndex={currentRailIndex} onSelect={stage.selectTurn} />
          <div
            ref={canvasScrollRef}
            className="stage-canvas h-full overflow-y-auto overflow-x-hidden"
          >
            {canvasNotice}
            {header}
            <div className="relative flex-1">
              <div
                className="stage-page pb-48 pt-8"
                data-stage-page
                style={
                  dockHeight
                    ? {
                        paddingBottom: `calc(${dockHeight}px + 1.5rem + var(--stage-mobile-bottom-clearance, 0px))`,
                      }
                    : undefined
                }
              >
                <div className="mx-auto w-full max-w-container-content">
                  <StageCanvasTransition pageKey={stage.view.key}>
                    <StagePage
                      view={stage.view}
                      turns={turns}
                      request={turns[stage.shownTurnIndex]?.request ?? ''}
                      loadingLine={loadingLine}
                      onSubmit={() => stage.followFromCurrentView()}
                    />
                  </StageCanvasTransition>
                </div>
              </div>
            </div>
          </div>
          {errorNotice && store.lastRunError?.responseId !== dismissedErrorRunId ? (
            <div
              className="stage-floating-notice pointer-events-none absolute inset-x-0 z-[6] flex justify-center px-5"
              style={{ bottom: dockHeight + 8 }}
            >
              <div
                aria-live="polite"
                className="pointer-events-auto flex max-w-container-form items-start gap-3 rounded-xl border border-destructive/30 bg-background/95 px-4 py-3 text-sm text-foreground shadow-lg backdrop-blur"
              >
                <span>{errorNotice}</span>
                <button
                  type="button"
                  aria-label={intl.formatMessage(messages.dismiss)}
                  onClick={() => setDismissedErrorRunId(store.lastRunError?.responseId ?? null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  &times;
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <StageActivity
          open={activityOpen}
          responseId={activityContext.responseId}
          request={activityContext.request}
          turns={turns}
          sharedResponse={activityContext.sharedResponse}
          viewingHistory={viewingHistory}
          liveStatus={
            runInFlight ? activityStatus || intl.formatMessage(messages.working) : undefined
          }
          triggerRef={activityTriggerRef}
          onClose={() => setActivityOpen(false)}
          onViewLive={() => setSelectedIndex(null)}
          navigableSurfaceIds={activitySurfaceNavigationTargets(
            turns,
            surfaceEntries.map(([id]) => id),
            stage.view.kind === 'surface' ? stage.view.surfaceId : null,
          )}
          onViewSurface={(surfaceId) => {
            const index = activitySurfaceTurnIndex(turns, surfaceId);
            if (index >= 0) {
              stage.selectTurn(index);
            }
          }}
        />
        {renderDock ? renderDock(dockProps) : <StageDock {...dockProps} />}
      </div>
    </ComposerDraftContext.Provider>
  );
});
